import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LiquidacionDesglose } from '@/components/LiquidacionDesglose';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import {
  decimalSolicitud,
  entradaDecimal,
  formatoDecimal,
  formatoEuros,
  type LiquidacionPreparacion,
  type LiquidacionSolicitud,
  type LiquidacionSnapshot,
  type ModoLiquidacion,
} from '@/lib/liquidacion';
import { useCamada, useCamadasListado } from '@/lib/queries/camadas';
import {
  useGuardarLiquidacion,
  useLiquidacion,
  useLiquidacionPreview,
} from '@/lib/queries/liquidacion';
import { useAuthStore } from '@/stores/auth';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import { currentBundle } from '@/lib/offline/bundle';
import { Decimal } from '@/lib/offline/decimal';
import LiquidacionesListado from './LiquidacionesListado';

const campos = [
  'fecha_final',
  'edad_entrada',
  'kg_entrada',
  'kg_salida',
  'kg_pienso',
  'ajuste_carga_eur',
  'bonificacion_eur',
] as const;
type Campo = (typeof campos)[number];
type Formulario = Record<Campo, string>;

function formulario(datos: LiquidacionPreparacion['datos']): Formulario {
  return Object.fromEntries(
    campos.map((campo) => [
      campo,
      campo === 'fecha_final' ? (datos[campo] ?? '') : entradaDecimal(datos[campo]),
    ]),
  ) as Formulario;
}

function mismosDatos(form: Formulario, datos: LiquidacionPreparacion['datos']) {
  return campos.every((campo) => {
    const valor = form[campo].trim();
    if (datos[campo] === null) return valor === '';
    if (campo === 'fecha_final') return valor === datos[campo];
    if (campo === 'edad_entrada')
      return (
        /^\d+$/.test(valor) && Number.isSafeInteger(Number(valor)) && Number(valor) === datos[campo]
      );
    try {
      const decimal = decimalSolicitud(valor).replace(
        /^([+-]?)\./,
        (_, sign: string) => `${sign}0.`,
      );
      return new Decimal(decimal).compare(datos[campo]) === 0;
    } catch {
      return false;
    }
  });
}

function mismoValor(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b))
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((valor, i) => mismoValor(valor, b[i]))
    );
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const left = a as Record<string, unknown>,
      right = b as Record<string, unknown>;
    const keys = Object.keys(left);
    return (
      keys.length === Object.keys(right).length &&
      keys.every((key) => Object.hasOwn(right, key) && mismoValor(left[key], right[key]))
    );
  }
  if (
    (typeof a === 'number' || typeof a === 'string') &&
    (typeof b === 'number' || typeof b === 'string')
  ) {
    try {
      return new Decimal(a).compare(b) === 0;
    } catch {
      return false;
    }
  }
  return false;
}

function mismoSnapshot(a: LiquidacionSnapshot, b: LiquidacionSnapshot) {
  return (['datos', 'fuentes', 'configuracion', 'resultado'] as const).every((campo) =>
    mismoValor(a[campo], b[campo]),
  );
}

export default function LiquidacionPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { camadaId } = useParams();
  if (user?.rol === 'operario')
    return (
      <main className="mx-auto max-w-md px-4">
        <h1 className="text-xl font-bold">{t('liquidacion.titulo')}</h1>
        <p role="alert" className="mt-4 text-warning-text">
          {t('liquidacion.sin_permiso')}
        </p>
      </main>
    );
  return camadaId ? <LiquidacionSeleccion /> : <LiquidacionesListado />;
}

function LiquidacionSeleccion() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { camadaId } = useParams();
  const owner = useAuthStore((s) => s.user?.id);
  useEntityMappings();
  const farm = currentBundle()?.explotacion_id ?? null;
  const context = useRef({ owner, camadaId, farm, revision: 0 });
  if (context.current.owner !== owner || context.current.camadaId !== camadaId)
    context.current = { owner, camadaId, farm, revision: 0 };
  else if (farm != null) {
    if (context.current.farm != null && context.current.farm !== farm) context.current.revision++;
    context.current.farm = farm;
  }
  const id = useEntityId(camadaId, 'camadas');
  const camadas = useCamadasListado();
  const selected = useCamada(
    camadas.data && !camadas.data.items.some((c) => c.id === id) ? id : null,
  );
  const opciones = [...(camadas.data?.items ?? [])];
  if (selected.data && !opciones.some((c) => c.id === selected.data.id))
    opciones.push(selected.data);
  const q = useLiquidacion(id);
  return (
    <main className="mx-auto max-w-md px-4 pb-8">
      <h1 className="text-xl font-bold">{t('liquidacion.titulo')}</h1>
      <p className="mt-1 text-sm text-text-secondary">{t('liquidacion.intro')}</p>
      <Link
        to="/liquidacion"
        className="mt-3 inline-flex min-h-11 items-center text-sm text-primary"
      >
        {t('liquidacion.volver_listado')}
      </Link>
      <label className="mt-5 block text-sm font-medium">
        {t('liquidacion.camada')}
        <select
          value={id ?? ''}
          onChange={(e) =>
            navigate(e.target.value ? `/liquidacion/${e.target.value}` : '/liquidacion')
          }
          className="mt-1 min-h-11 w-full rounded-lg border border-border bg-surface px-3"
        >
          <option value="">{t('liquidacion.seleccionar')}</option>
          {opciones.map((c) => (
            <option key={c.id} value={c.id}>
              {c.codigo_lote} · {t(`pienso_ajustes.${c.sexo}`)} ·{' '}
              {t(c.cerrada ? 'camadas.card_estado_cerrada' : 'camadas.card_estado_activa')}
            </option>
          ))}
        </select>
      </label>
      {camadas.isError && (
        <p role="alert" className="mt-3 text-warning-text">
          {extractApiMessage(camadas.error)}
        </p>
      )}
      {camadas.data?.total === 0 && (
        <p className="mt-4 text-text-secondary">{t('liquidacion.sin_camadas')}</p>
      )}
      {id !== null && q.isLoading && (
        <p className="mt-5 text-text-secondary">{t('common.loading')}</p>
      )}
      {q.isError && (
        <p role="alert" className="mt-5 text-warning-text">
          {extractApiMessage(q.error)}
        </p>
      )}
      {id !== null && q.data && (
        <LiquidacionEditor
          key={`${owner}:${camadaId}:${context.current.revision}`}
          id={id}
          preparacion={q.data}
          refrescar={async () => {
            const response = await q.refetch();
            if (response.error || !response.data)
              throw response.error ?? new Error(t('common.error'));
            return response.data;
          }}
        />
      )}
    </main>
  );
}

export function LiquidacionEditor({
  id,
  preparacion,
  refrescar,
  wizard,
}: {
  id: number;
  preparacion: LiquidacionPreparacion;
  refrescar: () => Promise<LiquidacionPreparacion>;
  wizard?: {
    onBack: () => void;
    onFinish: () => void;
    onDefer?: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onBusyChange: (busy: boolean) => void;
  };
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const mounted = useRef(true);
  const context = useRef({ owner: user?.id, farm: currentBundle()?.explotacion_id ?? null });
  const contextCurrent = useCallback(
    () =>
      mounted.current &&
      context.current.owner === useAuthStore.getState().user?.id &&
      (context.current.farm == null || context.current.farm === currentBundle()?.explotacion_id),
    [],
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const puedeGuardar = user?.rol === 'admin' || user?.rol === 'ganadero';
  const [guardada, setGuardada] = useState(preparacion.guardada);
  const [versionBase, setVersionBase] = useState(preparacion.guardada?.version ?? 0);
  const [modo, setModo] = useState<ModoLiquidacion>(guardada ? 'guardada' : 'nueva');
  const [form, setForm] = useState(() => formulario(guardada?.snapshot.datos ?? preparacion.datos));
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(() =>
    campos.some((campo) => !form[campo].trim()),
  );
  const editado =
    modo === 'actualizar_registros' ||
    !mismosDatos(form, guardada?.snapshot.datos ?? preparacion.datos);
  const [errorGuardar, setErrorGuardar] = useState<string | null>(null);
  const [actualizando, setActualizando] = useState(false);
  const lock = useRef(false);
  const guardar = useGuardarLiquidacion(id);
  const versionCambio = !guardar.isPending && (preparacion.guardada?.version ?? 0) !== versionBase;
  const [espera, setEspera] = useState<LiquidacionSolicitud | null>(null);
  const informacion = modo === 'guardada' && guardada ? guardada.snapshot.datos : preparacion.datos;
  const errores = useMemo(
    () =>
      Object.fromEntries(
        campos.flatMap((campo) => {
          const valor = form[campo].trim();
          if (!valor) return [[campo, t('liquidacion.obligatorio')]];
          if (campo === 'fecha_final') return [];
          if (campo === 'edad_entrada')
            return /^\d+$/.test(valor) && Number.isSafeInteger(Number(valor))
              ? []
              : [[campo, t('liquidacion.entero')]];
          return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(decimalSolicitud(valor))
            ? []
            : [[campo, t('liquidacion.numero')]];
        }),
      ),
    [form, t],
  );
  const solicitud = useMemo<LiquidacionSolicitud | null>(() => {
    if (actualizando || versionCambio || Object.keys(errores).length) return null;
    return {
      modo,
      version_esperada: versionBase,
      cambios: {
        fecha_final: form.fecha_final,
        edad_entrada: Number(form.edad_entrada),
        kg_entrada: decimalSolicitud(form.kg_entrada),
        kg_salida: decimalSolicitud(form.kg_salida),
        kg_pienso: decimalSolicitud(form.kg_pienso),
        ajuste_carga_eur: decimalSolicitud(form.ajuste_carga_eur),
        bonificacion_eur: decimalSolicitud(form.bonificacion_eur),
      },
    };
  }, [form, modo, errores, actualizando, versionCambio, versionBase]);
  useEffect(() => {
    setEspera(null);
    if (!solicitud || (!editado && guardada)) return;
    const timer = setTimeout(() => setEspera(solicitud), 250);
    return () => clearTimeout(timer);
  }, [solicitud, editado, guardada]);
  const preview = useLiquidacionPreview(id, espera);
  const actual =
    espera === solicitud && preview.isSuccess && !preview.isFetching ? preview.data : null;
  const busy = guardar.isPending || actualizando;
  const unchanged =
    !!guardada &&
    !versionCambio &&
    (!editado || (!!actual && mismoSnapshot(actual, guardada.snapshot)));
  const visible = versionCambio ? null : unchanged && guardada ? guardada.snapshot : actual;
  const dirty = editado && !unchanged;
  const onDirtyChange = wizard?.onDirtyChange;
  const onBusyChange = wizard?.onBusyChange;
  useEffect(() => {
    if (contextCurrent()) onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange, contextCurrent]);
  useEffect(() => {
    if (contextCurrent()) onBusyChange?.(busy);
  }, [busy, onBusyChange, contextCurrent]);

  function adoptarCopia(data: LiquidacionPreparacion, conservarEdicion: boolean) {
    setGuardada(data.guardada);
    setVersionBase(data.guardada?.version ?? 0);
    setModo(data.guardada ? 'guardada' : 'nueva');
    if (!conservarEdicion) {
      setForm(formulario(data.guardada?.snapshot.datos ?? data.datos));
    }
    setErrorGuardar(null);
  }

  async function guardarCambios() {
    if (lock.current || busy || versionCambio || !contextCurrent()) return;
    if (
      guardada &&
      ((modo === 'guardada' && mismosDatos(form, guardada.snapshot.datos)) ||
        (actual && mismoSnapshot(actual, guardada.snapshot)))
    ) {
      wizard?.onDirtyChange(false);
      wizard?.onFinish();
      return;
    }
    if (!actual || !solicitud || !puedeGuardar || !preparacion.cerrada || errorGuardar) return;
    lock.current = true;
    wizard?.onBusyChange(true);
    setErrorGuardar(null);
    try {
      const data = await guardar.mutateAsync({
        ...solicitud,
        token_preview: actual.token_preview,
        version_esperada: actual.version_esperada,
        ...(actual.local_preview
          ? {
              approved_snapshot: {
                datos: actual.datos,
                fuentes: actual.fuentes,
                configuracion: actual.configuracion,
                resultado: actual.resultado,
              },
            }
          : {}),
      });
      if (!contextCurrent()) return;
      adoptarCopia(data, false);
      toast.show({
        kind: 'success',
        message: t('liquidacion.guardado'),
      });
      wizard?.onDirtyChange(false);
      lock.current = false;
      wizard?.onBusyChange(false);
      wizard?.onFinish();
    } catch (err) {
      if (contextCurrent()) setErrorGuardar(extractApiMessage(err));
    } finally {
      lock.current = false;
      if (contextCurrent()) wizard?.onBusyChange(false);
    }
  }
  async function revisarDeNuevo() {
    if (lock.current || !contextCurrent()) return;
    lock.current = true;
    wizard?.onBusyChange(true);
    setActualizando(true);
    setErrorGuardar(null);
    try {
      const data = await refrescar();
      if (!contextCurrent()) return;
      adoptarCopia(data, true);
      // Al terminar la revisión, la solicitud genera un preview nuevo.
    } catch (err) {
      if (contextCurrent()) setErrorGuardar(extractApiMessage(err));
    } finally {
      lock.current = false;
      if (contextCurrent()) setActualizando(false);
    }
  }
  async function refrescarRegistros() {
    if (lock.current || !contextCurrent()) return;
    lock.current = true;
    wizard?.onBusyChange(true);
    setActualizando(true);
    setErrorGuardar(null);
    try {
      const actuales = await refrescar();
      if (!contextCurrent()) return;
      setGuardada(actuales.guardada);
      setVersionBase(actuales.guardada?.version ?? 0);
      setModo('actualizar_registros');
      setForm((formularioActual) => ({
        ...formulario(actuales.datos),
        ajuste_carga_eur: formularioActual.ajuste_carga_eur,
        bonificacion_eur: formularioActual.bonificacion_eur,
      }));
    } catch (err) {
      if (contextCurrent()) setErrorGuardar(extractApiMessage(err));
    } finally {
      lock.current = false;
      if (contextCurrent()) setActualizando(false);
    }
  }
  const datosYAjustes = (
    <>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        {[
          [t('liquidacion.sexo'), t(`pienso_ajustes.${informacion.sexo}`)],
          [
            t('liquidacion.fecha_entrada'),
            informacion.fecha_entrada.split('-').reverse().join('/'),
          ],
          [t('liquidacion.aves'), formatoDecimal((actual?.datos ?? informacion).aves_entradas, 0)],
          [
            t('liquidacion.bajas'),
            formatoDecimal((actual?.datos ?? informacion).bajas_registradas, 0),
          ],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-text-secondary">{label}</dt>
            <dd className="font-medium tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {guardada && <ComparacionFuentes snapshot={guardada.snapshot} actuales={preparacion.datos} />}
      {guardada && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void refrescarRegistros()}
          className="min-h-11 w-full rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-surface disabled:opacity-60"
        >
          {t('liquidacion.actualizar_registros')}
        </button>
      )}
      <fieldset disabled={busy} className="grid grid-cols-2 gap-4">
        <legend className="mb-3 font-semibold">{t('liquidacion.datos_calculo')}</legend>
        {campos.map((campo) => (
          <div key={campo} className={campo === 'fecha_final' ? 'col-span-2' : ''}>
            <label htmlFor={`liq-${campo}`} className="mb-1 block text-sm font-medium">
              {t(`liquidacion.${campo}`)}
            </label>
            <input
              id={`liq-${campo}`}
              type={campo === 'fecha_final' ? 'date' : 'text'}
              inputMode={campo === 'edad_entrada' ? 'numeric' : 'decimal'}
              min={campo === 'fecha_final' ? informacion.fecha_entrada : undefined}
              value={form[campo]}
              aria-invalid={!!errores[campo]}
              aria-describedby={`liq-ayuda-${campo}`}
              onChange={(e) => {
                if (lock.current) return;
                setForm((actuales) => ({ ...actuales, [campo]: e.target.value }));
                setErrorGuardar(null);
              }}
              className="min-h-11 w-full min-w-0 rounded-lg border border-border bg-surface px-3 py-2 text-base tabular-nums focus-visible:outline-accent"
            />
            <p id={`liq-ayuda-${campo}`} className="mt-1 text-xs text-text-secondary">
              {errores[campo] ??
                (campo === 'kg_entrada' || campo === 'kg_salida' || campo === 'kg_pienso'
                  ? t('liquidacion.registrado', {
                      valor: formatoDecimal(preparacion.datos[campo], 0),
                    })
                  : t('liquidacion.solo_liquidacion'))}
            </p>
          </div>
        ))}
      </fieldset>
    </>
  );
  return (
    <div className={wizard ? 'space-y-4 pb-44' : 'mt-5 space-y-5'} aria-busy={busy}>
      {wizard && (
        <section className="rounded-2xl bg-ok-bg p-4" aria-label="Importe de la liquidación">
          <div className="flex justify-between gap-3 text-sm">
            <h2 className="font-medium">
              {unchanged
                ? preparacion.local_pending
                  ? 'Liquidación pendiente de sincronizar'
                  : t('liquidacion.resultado_guardado')
                : visible
                  ? t('liquidacion.vista_previa')
                  : 'Datos pendientes'}
            </h2>
            <span className="shrink-0">{unchanged ? `v${guardada?.version}` : 'Sin guardar'}</span>
          </div>
          <p className="mt-2 break-words text-4xl font-semibold tabular-nums text-accent-icon">
            {visible ? formatoEuros(visible.resultado.total_eur) : '—'}
          </p>
          <p className="mt-2 text-sm text-text-secondary">
            {form.kg_pienso.trim() && !errores.kg_pienso
              ? `Consumo: ${formatoDecimal(decimalSolicitud(form.kg_pienso), 0)} kg`
              : 'Consumo pendiente'}
          </p>
        </section>
      )}
      {versionCambio && (
        <div
          role="alert"
          className="space-y-2 rounded-lg border border-warning-line bg-warning-bg p-3 text-sm text-warning-text"
        >
          <p>{t('liquidacion.copia_cambiada')}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (lock.current) return;
              adoptarCopia(preparacion, editado);
            }}
            className="min-h-11 font-medium underline"
          >
            {t('liquidacion.revisar_copia')}
          </button>
        </div>
      )}
      <p className="text-sm text-text-secondary">
        {t(guardada ? 'liquidacion.copia_conservada' : 'liquidacion.nueva_descripcion')}
      </p>
      {!preparacion.cerrada && (
        <p className="rounded-lg border border-warning-line bg-warning-bg p-3 text-sm text-warning-text">
          {t('liquidacion.solo_simulacion')}
        </p>
      )}
      {wizard ? (
        <details
          className="rounded-xl border border-border bg-surface p-4"
          open={adjustmentsOpen}
          onToggle={(event) => setAdjustmentsOpen(event.currentTarget.open)}
        >
          <summary className="min-h-12 cursor-pointer content-center font-medium">
            Datos y ajustes
          </summary>
          <div className="mt-3 space-y-5">{datosYAjustes}</div>
        </details>
      ) : (
        datosYAjustes
      )}
      {preview.isError && espera === solicitud && (
        <p role="alert" className="text-sm text-warning-text">
          {extractApiMessage(preview.error)}
        </p>
      )}
      {(actualizando || (solicitud && !visible && !(preview.isError && espera === solicitud))) && (
        <p className="text-sm text-text-secondary" role="status">
          {t('liquidacion.calculando')}
        </p>
      )}
      {visible &&
        (wizard ? (
          <details className="border-t border-border pt-2">
            <summary className="min-h-12 cursor-pointer content-center font-medium">
              Desglose del cálculo
            </summary>
            <LiquidacionDesglose snapshot={visible} />
          </details>
        ) : (
          <>
            <h2 className="font-semibold">
              {unchanged && guardada && preparacion.local_pending
                ? 'Liquidación pendiente de sincronizar'
                : t(
                    unchanged && guardada
                      ? 'liquidacion.resultado_guardado'
                      : 'liquidacion.vista_previa',
                  )}
            </h2>
            <LiquidacionDesglose snapshot={visible} />
          </>
        ))}
      {errorGuardar && (
        <div role="alert" className="space-y-2 text-sm text-warning-text">
          <p>{errorGuardar}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void revisarDeNuevo()}
            className="min-h-11 underline"
          >
            {t('liquidacion.revisar')}
          </button>
        </div>
      )}
      {!wizard && puedeGuardar && preparacion.cerrada && (
        <button
          type="button"
          disabled={unchanged || !actual || busy || !!errorGuardar}
          onClick={() => void guardarCambios()}
          className="min-h-11 w-full rounded-lg bg-accent px-4 py-2 font-semibold text-accent-contrast disabled:opacity-60"
        >
          {t(guardar.isPending ? 'liquidacion.guardando' : 'liquidacion.guardar')}
        </button>
      )}
      {wizard ? (
        <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <div className="mx-auto max-w-md space-y-2">
            <div className="flex gap-3">
              <button
                type="button"
                className="ui-secondary"
                disabled={busy}
                onClick={() => !lock.current && wizard.onBack()}
              >
                Atrás
              </button>
              <button
                type="button"
                className="ui-primary min-w-0 flex-1"
                disabled={
                  busy ||
                  versionCambio ||
                  (!unchanged &&
                    (!actual || !!errorGuardar || !puedeGuardar || !preparacion.cerrada))
                }
                onClick={() => void guardarCambios()}
              >
                {unchanged
                  ? 'Terminar'
                  : t(guardar.isPending ? 'liquidacion.guardando' : 'liquidacion.guardar')}
              </button>
            </div>
            {!unchanged && (
              <button
                type="button"
                className="ui-text-button min-h-11"
                disabled={busy}
                onClick={() => !lock.current && (wizard.onDefer ?? wizard.onFinish)()}
              >
                Liquidar más tarde
              </button>
            )}
          </div>
        </footer>
      ) : (
        <div className="flex flex-wrap justify-between gap-3 text-sm">
          <Link
            to={`/camadas/${id}`}
            className="min-h-11 content-center text-accent hover:underline"
          >
            {t('liquidacion.ver_camada')}
          </Link>
          <Link
            to="/ajustes/liquidacion"
            className="min-h-11 content-center text-accent hover:underline"
          >
            {t('liquidacion.ajustes')}
          </Link>
        </div>
      )}
    </div>
  );
}

function ComparacionFuentes({
  snapshot,
  actuales,
}: {
  snapshot: NonNullable<LiquidacionPreparacion['guardada']>['snapshot'];
  actuales: LiquidacionPreparacion['datos'];
}) {
  const { t } = useTranslation();
  const camposFuente = [
    'fecha_entrada',
    'fecha_final',
    'edad_entrada',
    'aves_entradas',
    'bajas_registradas',
    'kg_entrada',
    'kg_salida',
    'kg_pienso',
  ] as const;
  type CampoFuente = (typeof camposFuente)[number];
  function valor(campo: CampoFuente, data: LiquidacionPreparacion['datos']) {
    const value = data[campo];
    if (value === null) return '—';
    if (campo === 'fecha_entrada' || campo === 'fecha_final')
      return String(value).split('-').reverse().join('/');
    return campo === 'aves_entradas' || campo === 'bajas_registradas'
      ? formatoDecimal(value, 0)
      : entradaDecimal(value);
  }
  const diferentes = camposFuente.filter(
    (campo) =>
      valor(campo, snapshot.datos) !== valor(campo, actuales) ||
      valor(campo, snapshot.fuentes) !== valor(campo, actuales),
  );
  if (!diferentes.length) return null;
  return (
    <section
      aria-label={t('liquidacion.diferencias')}
      className="space-y-3 border-t border-border pt-3 text-sm"
    >
      <h2 className="font-semibold">{t('liquidacion.diferencias')}</h2>
      <p className="text-xs text-text-secondary">{t('liquidacion.diferencias_ayuda')}</p>
      {diferentes.map((campo) => (
        <div key={campo}>
          <h3 className="mb-1 font-medium">
            {t(
              `liquidacion.${campo === 'aves_entradas' ? 'aves' : campo === 'bajas_registradas' ? 'bajas' : campo}`,
            )}
          </h3>
          <dl className="grid grid-cols-3 gap-2 text-xs">
            {[
              [t('liquidacion.usado'), snapshot.datos],
              [t('liquidacion.al_guardar'), snapshot.fuentes],
              [t('liquidacion.actual'), actuales],
            ].map(([label, data]) => (
              <div className="min-w-0" key={label as string}>
                <dt className="text-text-secondary">{label as string}</dt>
                <dd className="break-words tabular-nums">
                  {valor(campo, data as LiquidacionPreparacion['datos'])}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </section>
  );
}
